import { useState, useEffect } from "react";
import { Zap, Loader2, ArrowRight, TrendingUp, Users } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadTable from "@/components/LeadTable";
import { toast } from "sonner";

const PRESET_INDUSTRIES = [
  "SaaS / Software", "Fintech", "E-commerce", "Healthcare", "Marketing",
  "Sales Tech", "HR Tech", "Logistics", "Real Estate", "EdTech",
];

export default function TriggerEvents() {
  const [industry, setIndustry] = useState("");
  const [signal, setSignal] = useState("funding");
  const [location, setLocation] = useState("");
  const [count, setCount] = useState("10");
  const [campaignId, setCampaignId] = useState("none");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const search = async () => {
    if (!industry.trim()) { toast.error("Enter an industry to search"); return; }
    setLoading(true);
    setResults([]);
    try {
      const r = await api.post("/leads/trigger-search", {
        industry: industry.trim(),
        signal,
        location: location.trim() || undefined,
        count: parseInt(count),
        campaign_id: campaignId !== "none" ? campaignId : undefined,
      });
      setResults(r.data);
      toast.success(`Found ${r.data.length} high-intent companies`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="label-mono mb-2">— Lead Generation</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Trigger Events</h1>
        <p className="text-[#5C5C66] mt-2">Find companies that just raised funding or are actively hiring — they're primed to buy.</p>
      </div>

      {/* Signal cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <button
          onClick={() => setSignal("funding")}
          className={`p-5 rounded-2xl border-2 text-left transition-all ${
            signal === "funding"
              ? "border-[#002FA7] bg-[#002FA7]/5"
              : "border-[#EAEAE3] bg-white hover:border-[#002FA7]/40"
          } shadow-soft`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${signal === "funding" ? "bg-[#002FA7] text-white" : "bg-[#F4F4EE] text-[#5C5C66]"}`}>
            <TrendingUp className="h-5 w-5" />
          </div>
          <div className="font-display text-lg font-bold tracking-tighter mb-1">Funding signal</div>
          <p className="text-sm text-[#5C5C66]">Companies that recently raised a funding round. They have budget and are growing fast.</p>
        </button>
        <button
          onClick={() => setSignal("hiring")}
          className={`p-5 rounded-2xl border-2 text-left transition-all ${
            signal === "hiring"
              ? "border-[#FF5722] bg-[#FF5722]/5"
              : "border-[#EAEAE3] bg-white hover:border-[#FF5722]/40"
          } shadow-soft`}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${signal === "hiring" ? "bg-[#FF5722] text-white" : "bg-[#F4F4EE] text-[#5C5C66]"}`}>
            <Users className="h-5 w-5" />
          </div>
          <div className="font-display text-lg font-bold tracking-tighter mb-1">Hiring signal</div>
          <p className="text-sm text-[#5C5C66]">Companies actively expanding their team. Growth stage = buying intent.</p>
        </button>
      </div>

      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="lg:col-span-2">
            <Label className="label-mono">Industry</Label>
            <Input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="e.g. SaaS / Software"
              className="mt-2 rounded-lg"
              list="industry-presets"
              data-testid="trigger-industry"
            />
            <datalist id="industry-presets">
              {PRESET_INDUSTRIES.map((ind) => <option key={ind} value={ind} />)}
            </datalist>
          </div>
          <div>
            <Label className="label-mono">Location (optional)</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. United States"
              className="mt-2 rounded-lg"
            />
          </div>
          <div>
            <Label className="label-mono">Number of leads</Label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger className="mt-2 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "15", "20", "25"].map((n) => <SelectItem key={n} value={n}>{n} leads</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-4">
          <Label className="label-mono">Save to campaign</Label>
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger className="mt-2 rounded-lg w-72"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Don't save to campaign</SelectItem>
              {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={search} disabled={loading}
          className={`rounded-lg text-white shadow-soft ${signal === "funding" ? "bg-[#002FA7] hover:bg-[#1E4FD6]" : "bg-[#FF5722] hover:bg-[#E64A19]"}`}
          data-testid="trigger-search-btn">
          {loading
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Searching for {signal === "funding" ? "funded" : "hiring"} companies…</>
            : <><Zap className="h-4 w-4 mr-2" /> Find {signal} companies <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>

      {loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-12 text-center shadow-soft">
          <Loader2 className="h-8 w-8 animate-spin text-[#002FA7] mx-auto mb-4" />
          <div className="font-display text-lg font-bold tracking-tighter">
            Scanning for {signal === "funding" ? "recently funded" : "actively hiring"} companies…
          </div>
          <p className="text-[#5C5C66] text-sm mt-2">AI is surfacing high-intent prospects in {industry}.</p>
        </div>
      )}

      {results.length > 0 && !loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="p-5 border-b border-[#EAEAE3]">
            <div className="font-display text-lg font-bold tracking-tighter">{results.length} high-intent companies found</div>
            <div className="text-xs text-[#5C5C66] mt-0.5">
              {signal === "funding" ? "Recently funded" : "Actively hiring"} · {industry}{location ? ` · ${location}` : ""}
            </div>
          </div>
          <LeadTable leads={results} onRefresh={() => {}} />
        </div>
      )}

      {!loading && results.length === 0 && (
        <div className="bg-[#FAFAF7] border border-dashed border-[#EAEAE3] rounded-2xl p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#EAEAE3] flex items-center justify-center mx-auto mb-4 shadow-soft">
            <Zap className="h-7 w-7 text-[#9A9AA3]" />
          </div>
          <div className="font-display text-xl font-bold tracking-tighter mb-2">Find companies showing buying intent</div>
          <p className="text-[#5C5C66] text-sm">Choose a signal above and enter an industry to find high-intent prospects.</p>
        </div>
      )}
    </div>
  );
}
